import { useFrame } from '@react-three/fiber'
import { useMemo, useRef } from 'react'
import { CanvasTexture, SRGBColorSpace, type Mesh, type MeshBasicMaterial } from 'three'
import type { EstadoBolaNoTempo } from '../../dominio/bleve'

const temCanvas = (): boolean => typeof document !== 'undefined' && typeof document.createElement === 'function'

/**
 * Textura mosqueada de chama: manchas irregulares sobre a cor base, geradas
 * uma vez em canvas (mesma técnica de `texturasSinalizacao.ts`). Fora do
 * navegador (testes em Node) devolve `null` e a esfera fica na cor sólida.
 */
function texturaChama(corBase: string, ...corasManchas: string[]): CanvasTexture | null {
  if (!temCanvas()) return null
  const canvas = document.createElement('canvas')
  canvas.width = 256
  canvas.height = 256
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.fillStyle = corBase
  ctx.fillRect(0, 0, 256, 256)
  for (let i = 0; i < 90; i++) {
    ctx.fillStyle = corasManchas[i % corasManchas.length]!
    ctx.globalAlpha = 0.18 + Math.random() * 0.24
    ctx.beginPath()
    ctx.arc(Math.random() * 256, Math.random() * 256, 10 + Math.random() * 34, 0, Math.PI * 2)
    ctx.fill()
  }
  const textura = new CanvasTexture(canvas)
  textura.colorSpace = SRGBColorSpace
  return textura
}

const CONTAGEM_FUMACA = 5

/**
 * Bola de fogo num instante da animação: esfera externa e núcleo com textura
 * mosqueada e leve cintilação, luz pontual que ilumina o chão e os prédios, e
 * fumaça subindo quando a bola começa a apagar — tudo ilustrativo, a física
 * mora em `estadoNoTempo` (dominio/bleve).
 */
export function BolaDeFogo3D({ estado }: { estado: EstadoBolaNoTempo }) {
  const texturaExterna = useMemo(() => texturaChama('#c23a0f', '#ff6a1a', '#ff3d1a', '#8a2208'), [])
  const texturaInterna = useMemo(() => texturaChama('#ff8a1a', '#fff1b8', '#ffc23d', '#ff6a1a'), [])
  const materialExterno = useRef<MeshBasicMaterial>(null)
  const materialInterno = useRef<MeshBasicMaterial>(null)
  const fumaca = useRef<Array<Mesh | null>>([])

  useFrame(({ clock }) => {
    const t = clock.elapsedTime
    const cintilar = 0.9 + 0.08 * Math.sin(t * 11) + 0.05 * Math.sin(t * 23 + 1)
    if (materialExterno.current) materialExterno.current.opacity = 0.55 * estado.intensidade * cintilar
    if (materialInterno.current) materialInterno.current.opacity = 0.75 * estado.intensidade * cintilar

    // fumaça: só sobe quando a bola começa a apagar (último quarto da duração, ver estadoNoTempo)
    const fase = 1 - estado.intensidade
    fumaca.current.forEach((mesh, i) => {
      if (!mesh) return
      mesh.visible = fase > 0 && estado.diametroM > 0
      if (!mesh.visible) return
      const sobe = Math.max(0, fase - i * 0.12)
      mesh.position.y = estado.alturaCentroM + estado.diametroM * 0.3 + sobe * estado.diametroM * 1.4
      mesh.scale.setScalar(Math.max(0.01, estado.diametroM * (0.35 + sobe * 0.9)))
      ;(mesh.material as MeshBasicMaterial).opacity = Math.max(0, 0.3 * (1 - sobe))
    })
  })

  if (estado.diametroM <= 0 || estado.intensidade <= 0) return null
  const raio = estado.diametroM / 2
  return (
    <group position={[0, estado.alturaCentroM, 0]}>
      <mesh raycast={() => null}>
        <sphereGeometry args={[raio, 48, 32]} />
        <meshBasicMaterial
          ref={materialExterno}
          map={texturaExterna}
          color={texturaExterna ? '#ffffff' : '#ff6a1a'}
          transparent
          opacity={0.55 * estado.intensidade}
          depthWrite={false}
        />
      </mesh>
      <mesh raycast={() => null}>
        <sphereGeometry args={[raio * 0.72, 40, 28]} />
        <meshBasicMaterial
          ref={materialInterno}
          map={texturaInterna}
          color={texturaInterna ? '#ffffff' : '#ffc23d'}
          transparent
          opacity={0.75 * estado.intensidade}
          depthWrite={false}
        />
      </mesh>
      <pointLight color="#ff9a3d" intensity={4 * estado.intensidade} distance={raio * 12} decay={0.6} />
      {Array.from({ length: CONTAGEM_FUMACA }, (_, i) => (
        <mesh
          key={i}
          ref={(mesh) => {
            fumaca.current[i] = mesh
          }}
          visible={false}
          raycast={() => null}
        >
          <sphereGeometry args={[1, 12, 10]} />
          <meshBasicMaterial color="#6b6b6b" transparent opacity={0} depthWrite={false} />
        </mesh>
      ))}
    </group>
  )
}
